import { Injectable } from '@angular/core';
import * as moment from 'moment';
import { AngularFirestore } from '@angular/fire/firestore';
import { HttpClient } from '@angular/common/http';
import { environment } from '@env/environment';

import {
  COMPUTASSIST_ORDER_SUFFIX,
  ComputAssistPharmacy,
  ComputAssistScriptRequest,
  ComputAssistOrderStatusRecord,
  ComputAssistSubmitResponse,
} from './models/computassist.model';
import { MedicationPrescription, ClinicalNewClient, SendComputAssistScript } from './api-client.service';
import { dropNullUndefined } from '@app/core/functions/drop-null-undefined';
import { buildEscriptOrderNumber } from '@app/core/functions/escript-order-number';

@Injectable({
  providedIn: 'root',
})
export class ComputAssistApiService {
  constructor(
    private firestore: AngularFirestore,
    private httpClient: HttpClient,
    private clinicalNewClient: ClinicalNewClient
  ) {}

  /**
   * Sends a prescription to the ComputAssist SmartSwitch eScripting platform.
   *
   * Flow:
   * 1. Build the ComputAssist JSON payload from the prescription
   * 2. Call Florence proxy endpoint (static IP required)
   * 3. Write initial pending status to Firestore (both paths)
   */
  async sendScriptToComputAssist(
    tenantId: string,
    prescription: MedicationPrescription,
    pharmacy: ComputAssistPharmacy,
    pdfUrl: string,
    encounterId: string
  ): Promise<void> {
    const pdfBlob = await this.httpClient.get(pdfUrl, { responseType: 'blob' }).toPromise();
    const pdfBase64 = await new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve((reader.result as string).split(',')[1]);
      reader.onerror = reject;
      reader.readAsDataURL(pdfBlob);
    });

    const scriptNumber = this.getScriptNumber(prescription);
    const payload = this.buildScriptPayload(prescription, pharmacy, pdfBase64, scriptNumber);

    const request: SendComputAssistScript = {
      ScriptRequest: JSON.stringify(payload),
    };

    const response = await this.clinicalNewClient.sendComputAssistScript(tenantId, encounterId, request).toPromise();

    const scriptResponse = JSON.parse(response.Data.ScriptResponse) as ComputAssistSubmitResponse;

    if (scriptResponse.code !== 0) {
      throw new Error(`ComputAssist rejected the script: ${scriptResponse.message}`);
    }

    // Write initial status + audit record to Firestore
    const sentAt = new Date().toISOString();
    const initialStatus: ComputAssistOrderStatusRecord = {
      statusCode: -1, // -1 = pending (awaiting webhook response from pharmacy)
      statusUpdatedAt: sentAt,
      statusHistory: [],
    };

    const practiceRef = this.getFirestoreDocForPrescription(prescription, scriptNumber);
    await practiceRef.set(
      {
        scriptNumber,
        encounterId,
        prescriptionId: prescription.MedicationPrescriptionId,
        practiceId: prescription.Patient?.PracticeId,
        patientId: prescription.Patient?.PatientId,
        ...initialStatus,
        createdAt: sentAt,
        submitResponse: dropNullUndefined(scriptResponse),
      },
      { merge: true }
    );

    // Top-level doc so the webhook can resolve the practiceId path. Deliberately NO status fields
    // here: if the webhook lands before this write, a merged statusCode:-1/statusHistory:[] would
    // clobber the real status. A missing statusCode renders as "Sent to pharmacy" anyway.
    this.firestore
      .collection('ComputAssistOrders')
      .doc(scriptNumber)
      .set(
        {
          scriptNumber,
          practiceId: prescription.Patient?.PracticeId,
          patientId: prescription.Patient?.PatientId,
          encounterId,
          prescriptionId: prescription.MedicationPrescriptionId,
          clinicalPracticeRequestPath: `ClinicalPractice/${prescription.Patient?.PracticeId}/ComputAssistRequests/${scriptNumber}`,
          createdAt: sentAt,
        },
        { merge: true }
      );
  }

  /** Generates the script number for a prescription (shared HBC-…-CA format). */
  getScriptNumber(prescription: MedicationPrescription): string {
    return buildEscriptOrderNumber(prescription, COMPUTASSIST_ORDER_SUFFIX);
  }

  private buildScriptPayload(
    prescription: MedicationPrescription,
    pharmacy: ComputAssistPharmacy,
    pdfBase64: string,
    scriptNumber: string
  ): ComputAssistScriptRequest {
    const p = prescription;
    const patient = p.Patient;
    const accountDetails = patient?.PatientAccountDetails;
    const mainMember = accountDetails?.MedicalAidMainMemberDetails;
    const patientDetails = patient?.PatientDetails;
    const provider = p.Provider;

    const isTestMode = !environment.production;

    return {
      vendorid: 'hbridge',
      pharmacy: { ramsno: pharmacy.ramsno },
      doctor: {
        name: provider?.TreatingDoctorName ?? '',
        practicename: provider?.PracticeName ?? '',
        practiceno: provider?.TreatingDoctorPracticeNumber ?? '',
        phoneno: provider?.CellphoneNumber ?? '',
        email: provider?.EmailAddress ?? '',
      },
      member: {
        mainidno: mainMember?.IdentityNo ?? '',
        name: mainMember?.FirstName ?? '',
        surname: mainMember?.Surname ?? '',
        title: mainMember?.Title ?? '',
        maindob: mainMember?.DateOfBirth ? moment(mainMember.DateOfBirth).format('YYYYMMDD') : '',
        gender: (mainMember?.Gender ?? '').substring(0, 1).toUpperCase(),
        cellno: mainMember?.ContactNo ?? '',
        email: mainMember?.EmailAddress ?? '',
        medaid1: accountDetails?.MedicalAidName ?? '',
        medaidno: accountDetails?.MedicalAidMembershipNumber ?? '',
      },
      patient: {
        idno: patientDetails?.IdentityNo ?? '',
        relation: accountDetails?.MedicalAidDependentCode === '01' ? '1' : '2',
        name: patientDetails?.FirstName ?? '',
        surname: patientDetails?.Surname ?? '',
        title: patientDetails?.Title ?? '',
        gender: (patientDetails?.Gender ?? '').substring(0, 1).toUpperCase(),
        dob: patientDetails?.DateOfBirth ? moment(patientDetails.DateOfBirth).format('YYYYMMDD') : '',
        depcode: accountDetails?.MedicalAidDependentCode ?? '01',
      },
      script: {
        sctno: scriptNumber,
        medaid: accountDetails?.MedicalAidName ?? '',
        scriptdate: moment().format('DD MMM YYYY HH:mm:ss'),
        test: isTestMode ? 'Y' : 'N',
        delivery: '0', // Default to Collect; UI can override
        itemcount: String(p.MedicationPrescriptionLines?.length ?? 0),
        items: (p.MedicationPrescriptionLines ?? []).map((line, index) => ({
          itemno: String(index + 1),
          mix: '0',
          napcd: line.NappiCode10 || '000' + line.NappiCode,
          descript: line.MedicationDescription ?? '',
          qty: String(line.Quantity ?? 0) + '.000',
          repeats: String(line.Repeat ?? 0),
          supply: String(line.Quantity ?? 0),
          icd10cd: line.Diagnosis?.length > 0 ? line.Diagnosis[0].DiagnosisCode : '',
          directions: line.Parameters?.['DosageDescription'] ?? '',
        })),
      },
      pdf: pdfBase64,
    };
  }

  private getFirestoreDocForPrescription(prescription: MedicationPrescription, scriptNumber: string) {
    const practiceId = prescription.Patient?.PracticeId;
    if (!practiceId) {
      throw new Error(
        `ComputAssistApiService: cannot resolve Firestore doc path — practiceId is empty. ` +
          `prescriptionId=${prescription.MedicationPrescriptionId}`
      );
    }
    return this.firestore.collection('ClinicalPractice').doc(practiceId).collection('ComputAssistRequests').doc(scriptNumber);
  }
}
